/**
 * Default modal content for the Process Steps Section Card with Modal variant.
 */

import {
	DEFAULT_BADGE_COLORS,
	DEFAULT_CTA_LABEL,
	generateStepId,
	getDefaultStepIcon,
} from './constants';

export const MODAL_BODY_CLASS = 'alteia-pss-modal-open';

export const BADGE_PALETTE = [
	{ name: 'Olive', color: DEFAULT_BADGE_COLORS[ 0 ] },
	{ name: 'Terracotta', color: DEFAULT_BADGE_COLORS[ 1 ] },
	{ name: 'Forest', color: DEFAULT_BADGE_COLORS[ 2 ] },
	{ name: 'Slate', color: DEFAULT_BADGE_COLORS[ 3 ] },
	{ name: 'Coral', color: DEFAULT_BADGE_COLORS[ 4 ] },
];

/**
 * @param {string} label Badge label.
 * @param {number} index Badge index.
 * @return {Object} Badge object.
 */
export function createBadge( label = '', index = 0 ) {
	return {
		label,
		color: DEFAULT_BADGE_COLORS[ index % DEFAULT_BADGE_COLORS.length ],
	};
}

/**
 * @param {Object} overrides Process-step overrides.
 * @return {Object} Modal process step.
 */
export function createModalProcessStep( overrides = {} ) {
	return {
		title: '',
		description: '',
		...overrides,
	};
}

/**
 * Build default modal content for a named structure.
 *
 * @param {string} title Card / structure title.
 * @return {Object} Modal content object.
 */
export function createDefaultModalContent( title = '' ) {
	return {
		whatTitle: title ? `What is ${ title }?` : 'What is this structure?',
		whatText: '',
		approachTitle: 'The Alteia approach',
		approachText: '',
		processSteps: [ 1, 2, 3, 4, 5 ].map( ( number ) =>
			createModalProcessStep( {
				title: `Step ${ number }`,
			} )
		),
		whyTitle: title
			? `Why investors use ${ title }`
			: 'Why investors use this structure',
		whyText: '',
		commonUsesTitle: 'Common uses',
		badges: DEFAULT_BADGE_COLORS.map( ( color ) => ( {
			label: '',
			color,
		} ) ),
	};
}

const MURABAHA_MODAL = {
	whatTitle: 'What is Murabaha?',
	whatText:
		'Murabaha is a Shariah-compliant cost-plus financing structure used in trade finance. It involves the purchase and subsequent sale of identifiable goods at a pre-agreed purchase price and profit margin, linking the investment to physical assets rather than the lending of cash.',
	approachTitle: 'The Alteia approach',
	approachText:
		'Alteia Capital structures Murabaha investments to provide exposure to trade transactions by taking legal ownership of the goods before selling them to an approved off-taker. This ownership is central to governance, risk management, and Shariah compliance.',
	processSteps: [
		createModalProcessStep( {
			title: 'Purchase',
			description:
				'Alteia Capital purchases agreed goods from a supplier at a pre-agreed price with clear contractual terms.',
		} ),
		createModalProcessStep( {
			title: 'Ownership',
			description:
				'Alteia Capital takes legal ownership of the goods, which is a key element of the investment structure throughout its lifecycle.',
		} ),
		createModalProcessStep( {
			title: 'Forward sale',
			description:
				'The goods are sold to an approved off-taker at a pre-agreed price, including the profit margin, under deferred payment terms.',
		} ),
		createModalProcessStep( {
			title: 'Counterparty commitment',
			description:
				'The off-taker provides an agreed advance payment to align interests and reduce investment exposure.',
		} ),
		createModalProcessStep( {
			title: 'Settlement',
			description:
				'Upon settlement of the deferred payment, the transaction is completed and investment proceeds are realised.',
		} ),
	],
	whyTitle: 'Why investors use Murabaha',
	whyText:
		'Murabaha offers exposure to short-duration trade finance linked to physical assets. Its clearly defined contractual framework and asset-backed nature make it a widely used structure in Islamic trade finance.',
	commonUsesTitle: 'Common uses',
	badges: [
		createBadge( 'Commodity trade', 0 ),
		createBadge( 'Import & export', 1 ),
		createBadge( 'Supply chain finance', 2 ),
		createBadge( 'Inventory finance', 3 ),
		createBadge( 'Working capital', 4 ),
	],
};

const MUSHARAKA_MODAL = {
	whatTitle: 'What is Musharaka?',
	whatText:
		'Musharaka is a Shariah-compliant partnership where two or more parties contribute capital to a venture. Profits are shared according to agreed terms, and losses are shared in proportion to capital contribution.',
	approachTitle: 'The Alteia approach',
	approachText:
		'Alteia Capital applies Musharaka to selected investment opportunities where partnership supports long-term value, with due diligence and monitoring throughout the lifecycle.',
	processSteps: [
		createModalProcessStep( {
			title: 'Opportunity identification',
			description:
				"A commercial opportunity is identified and assessed against Alteia Capital's investment criteria.",
		} ),
		createModalProcessStep( {
			title: 'Partnership agreement',
			description:
				'Investment terms, responsibilities and profit-sharing arrangements are agreed between the participating parties.',
		} ),
		createModalProcessStep( {
			title: 'Capital contribution',
			description:
				'Partners contribute capital in accordance with the agreed Musharaka structure and legal documentation.',
		} ),
		createModalProcessStep( {
			title: 'Business activity',
			description:
				'The underlying commercial activity is undertaken, with investment performance monitored throughout the agreed investment period.',
		} ),
		createModalProcessStep( {
			title: 'Profit distribution and exit',
			description:
				'Profits are distributed according to the agreed partnership terms. Upon completion of the investment, capital is returned and the partnership concludes in accordance with the contractual arrangements.',
		} ),
	],
	whyTitle: 'Why investors use Musharaka',
	whyText:
		'Musharaka enables participation alongside commercial partners in shared-ownership opportunities, aligning investment returns with underlying commercial activity.',
	commonUsesTitle: 'Common uses',
	badges: [
		createBadge( 'Business expansion', 0 ),
		createBadge( 'Growth capital', 1 ),
		createBadge( 'Strategic partnerships', 2 ),
		createBadge( 'Commercial & industrial projects', 3 ),
		createBadge( 'Asset acquisition', 4 ),
	],
};

const SUKUK_MODAL = {
	whatTitle: 'What is Sukuk?',
	whatText:
		'Sukuk are recognised Shariah-compliant investment certificates that provide investors with exposure to identifiable underlying assets or commercial activities. Unlike conventional bonds, Sukuk are structured around ownership interests in tangible assets, projects or investment activities, with returns generated from the performance of those underlying assets rather than interest payments.',
	approachTitle: 'The Alteia approach',
	approachText:
		'Alteia Capital structures Sukuk investments to provide institutional investors with access to diversified, asset-backed investment opportunities within a disciplined governance framework. Each investment is evaluated against defined commercial and regulatory criteria before implementation and monitored throughout its lifecycle.',
	processSteps: [
		createModalProcessStep( {
			title: 'Asset identification',
			description:
				"Suitable underlying assets or investment opportunities are identified and assessed against Alteia Capital's investment criteria.",
		} ),
		createModalProcessStep( {
			title: 'Structure development',
			description:
				'A Sukuk structure is established, defining the underlying assets, investment terms and governance framework.',
		} ),
		createModalProcessStep( {
			title: 'Certificate issuance',
			description:
				'Investment certificates are issued, providing investors with beneficial exposure to the underlying assets or investment activity.',
		} ),
		createModalProcessStep( {
			title: 'Investment management',
			description:
				'The underlying assets or projects are actively monitored and managed throughout the investment period.',
		} ),
		createModalProcessStep( {
			title: 'Income distribution & maturity',
			description:
				'Returns generated by the underlying assets are distributed to investors in accordance with the agreed investment structure. At maturity, the investment concludes in line with the contractual arrangements.',
		} ),
	],
	whyTitle: 'Why investors use Sukuk',
	whyText:
		'Sukuk provide investors with exposure to identifiable underlying assets through recognised investment certificates. They are commonly used to diversify portfolios with asset-backed investments while maintaining alignment with recognised Shariah principles.',
	commonUsesTitle: 'Common uses',
	badges: [
		createBadge( 'Infrastructure development', 0 ),
		createBadge( 'Corporate financing', 1 ),
		createBadge( 'Energy & utilities', 2 ),
		createBadge( 'Transport & logistics', 3 ),
		createBadge( 'Sustainable investment projects', 4 ),
	],
};

/**
 * Default cards for the Card with Modal variant.
 */
export const WITH_MODAL_DEFAULT_STEPS = [
	{
		id: 'capital-murabaha',
		iconId: 0,
		iconUrl: getDefaultStepIcon( 0 ),
		title: 'Murabaha',
		description:
			'Provides investors with exposure to trade finance through a recognised cost-plus financing structure linked to identifiable commercial transactions.',
		ctaLabel: DEFAULT_CTA_LABEL,
		modalTitle: 'Murabaha',
		modalContent: MURABAHA_MODAL,
	},
	{
		id: 'capital-musharaka',
		iconId: 0,
		iconUrl: getDefaultStepIcon( 1 ),
		title: 'Musharaka',
		description:
			'A partnership-based investment structure that enables investors to participate alongside commercial partners in agreed business activities and share in the resulting outcomes.',
		ctaLabel: DEFAULT_CTA_LABEL,
		modalTitle: 'Musharaka',
		modalContent: MUSHARAKA_MODAL,
	},
	{
		id: 'capital-sukuk',
		iconId: 0,
		iconUrl: getDefaultStepIcon( 2 ),
		title: 'Sukuk',
		description:
			'Provides exposure to underlying assets through investment certificates structured in accordance with recognised Shariah principles.',
		ctaLabel: DEFAULT_CTA_LABEL,
		modalTitle: 'Sukuk',
		modalContent: SUKUK_MODAL,
	},
];

export const VARIANT_DEFAULTS = {
	default: {
		heading: 'How we underwrite',
		subDescription:
			'Transactions are evaluated against the same criteria, assessed from three angles: the borrower, the transaction terms, and the protections built in for investors.',
		disableSubDescription: false,
		steps: [
			{
				iconId: 0,
				iconUrl: getDefaultStepIcon( 0 ),
				title: 'Murabaha',
				description:
					'Operating track record, financial standing, KYC and AML screening, and the quality of trading counterparties are assessed before a facility is considered. Where appropriate, on-site due diligence and direct engagement with the borrower form part of the evaluation process.',
			},
			{
				iconId: 0,
				iconUrl: getDefaultStepIcon( 1 ),
				title: 'Musharaka',
				description:
					'Tenor aligned to the trade cycle. Repayment source clearly identified. Security over underlying assets confirmed before approval.',
			},
			{
				iconId: 0,
				iconUrl: getDefaultStepIcon( 2 ),
				title: 'Sukuk',
				description:
					'Financial and operational covenants are built into every facility. Defined triggers, monitoring requirements, and remedies help protect investor capital throughout the life of the transaction.',
			},
		],
	},
	'with-modal': {
		heading: 'How we structure capital',
		subDescription:
			'Alteia Capital utilises recognised, Shariah-compliant frameworks to connect investor capital directly with real commercial activity. While each structure offers a distinct approach, all operate within the same disciplined investment framework.',
		disableSubDescription: false,
		steps: WITH_MODAL_DEFAULT_STEPS,
	},
};

/**
 * Create a blank Card with Modal step, including modal scaffolding.
 *
 * @param {number} [index] Step index for default icon.
 * @return {Object} Step object.
 */
export function createBlankModalStep( index = 0 ) {
	return {
		id: generateStepId( 'step' ),
		iconId: 0,
		iconUrl: getDefaultStepIcon( index ),
		title: '',
		description: '',
		ctaLabel: DEFAULT_CTA_LABEL,
		modalTitle: '',
		modalContent: createDefaultModalContent( '' ),
	};
}
